import { Box, IconButton, Popover } from '@ltvco/refresh-lib/theme';
import { Close } from '@mui/icons-material';
import React, { useState, useCallback, useEffect, useId } from 'react';

import {
  PopoverContainer,
  Header,
  Title,
  ContentBody,
  FieldLabel,
  StyledInput,
  Footer,
  ApplyButton,
  CancelButton,
} from './components';

// ============= TYPES =============
export interface TextFieldFilterPopoverProps {
  /** Anchor element for the popover */
  anchorEl: HTMLElement | null;
  /** Whether the popover is open */
  open: boolean;
  /** Callback when popover is closed */
  onClose: () => void;
  /** Title of the popover */
  title: string;
  /** Label for the text field */
  fieldLabel: string;
  /** Placeholder for the text field */
  placeholder?: string;
  /** Current value */
  currentValue: string;
  /** Callback when value is applied */
  onApply: (value: string) => void;
  /** Test ID for the component */
  testId?: string;
}

// ============= COMPONENT =============
export const TextFieldFilterPopover: React.FC<TextFieldFilterPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  title,
  fieldLabel,
  placeholder,
  currentValue,
  onApply,
  testId = 'text-field-filter-popover',
}) => {
  const labelId = useId();
  const inputId = useId();
  const [localValue, setLocalValue] = useState<string>(currentValue);

  useEffect(() => {
    if (open) {
      setLocalValue(currentValue);
    }
  }, [open, currentValue]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(event.target.value);
    },
    []
  );

  const handleApply = useCallback(() => {
    onApply(localValue);
    onClose();
  }, [localValue, onApply, onClose]);

  const handleCancel = useCallback(() => {
    setLocalValue(currentValue);
    onClose();
  }, [currentValue, onClose]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleApply();
      }
    },
    [handleApply]
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      marginThreshold={8}
      slotProps={{
        paper: {
          sx: (theme) => ({
            mt: 1,
            maxWidth: { xs: 'calc(100vw - 16px)', sm: 'none' },
            [theme.breakpoints.only('xs')]: {
              left: '8px',
              right: '8px',
            },
          }),
        },
      }}
      data-testid={testId}
    >
      <PopoverContainer>
        {/* Header */}
        <Header>
          <Title variant="h6">{title}</Title>
          <IconButton size="small" onClick={handleCancel} aria-label="Close">
            <Close />
          </IconButton>
        </Header>

        {/* Content Body */}
        <ContentBody>
          <Box>
            <FieldLabel id={labelId}>{fieldLabel}</FieldLabel>
            <StyledInput
              id={inputId}
              aria-labelledby={labelId}
              fullWidth
              value={localValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || `Enter ${fieldLabel.toLowerCase()}`}
            />
          </Box>
        </ContentBody>

        {/* Footer */}
        <Footer>
          <ApplyButton variant="contained" onClick={handleApply}>
            Apply
          </ApplyButton>
          <CancelButton variant="outlined" onClick={handleCancel}>
            Cancel
          </CancelButton>
        </Footer>
      </PopoverContainer>
    </Popover>
  );
};
